// @core/stores/bootstrap/BootstrapStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { bootstrapApi } from '@core/api';
import type {
  AcknowledgeVaultKeysRequest,
  AcknowledgeVaultKeysResponse,
  ExecuteBootstrapRequest,
  ExecuteBootstrapResponse,
  GetBootstrapStatusResponse,
  GetExecutionProgressResponse,
  GetSystemInfoResponse,
  GetWizardStepsResponse,
  ProbeVaultStatusResponse,
  ResetBootstrapRequest,
  ResetBootstrapResponse,
  RetryExecutionRequest,
  RetryExecutionResponse,
  SaveAdminAccountRequest,
  SaveAdminAccountResponse,
  SavePlatformIdentityRequest,
  SavePlatformIdentityResponse,
  SavePlatformModeRequest,
  SavePlatformModeResponse,
  SaveProductSelectionRequest,
  SaveProductSelectionResponse,
  SaveSecurityConfigRequest,
  SaveSecurityConfigResponse,
  SaveVaultConfigRequest,
  SaveVaultConfigResponse,
  TestAutoUnsealRequest,
  TestAutoUnsealResponse,
  TestLdapConnectionRequest,
  TestLdapConnectionResponse,
  TestOidcProviderRequest,
  TestOidcProviderResponse,
  ValidateSlugResponse,
  ValidateStepRequest,
  ValidateStepResponse,
} from '@core/types/bootstrap';

class BootstrapStore {

  // Loading states
  isLoading = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }

  reset(): void {
    this.error = null;
  }

  /**
   * Acknowledge vault keys saved
   */
  async AcknowledgeVaultKeys(data: AcknowledgeVaultKeysRequest): Promise<AcknowledgeVaultKeysResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.AcknowledgeVaultKeys(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Execute bootstrap
   */
  async Execute(data: ExecuteBootstrapRequest): Promise<ExecuteBootstrapResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.Execute(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get execution progress
   */
  async GetExecutionProgress(params?: { }): Promise<GetExecutionProgressResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.GetExecutionProgress(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get bootstrap status
   */
  async GetStatus(params?: { }): Promise<GetBootstrapStatusResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.GetStatus(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get system information
   */
  async GetSystemInfo(params?: { }): Promise<GetSystemInfoResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.GetSystemInfo(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get wizard steps
   */
  async GetWizardSteps(params?: { }): Promise<GetWizardStepsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.GetWizardSteps(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Probe vault status
   */
  async ProbeVaultStatus(params?: { }): Promise<ProbeVaultStatusResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.ProbeVaultStatus(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reset bootstrap
   */
  async Reset(data: ResetBootstrapRequest): Promise<ResetBootstrapResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.Reset(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Retry failed bootstrap
   */
  async RetryExecution(data: RetryExecutionRequest): Promise<RetryExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.RetryExecution(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Save admin account
   */
  async SaveAdminAccount(data: SaveAdminAccountRequest): Promise<SaveAdminAccountResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.SaveAdminAccount(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Save platform identity
   */
  async SavePlatformIdentity(data: SavePlatformIdentityRequest): Promise<SavePlatformIdentityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.SavePlatformIdentity(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Save platform mode
   */
  async SavePlatformMode(data: SavePlatformModeRequest): Promise<SavePlatformModeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.SavePlatformMode(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Save product selection
   */
  async SaveProductSelection(data: SaveProductSelectionRequest): Promise<SaveProductSelectionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.SaveProductSelection(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Save security configuration
   */
  async SaveSecurityConfig(data: SaveSecurityConfigRequest): Promise<SaveSecurityConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.SaveSecurityConfig(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Save vault configuration
   */
  async SaveVaultConfig(data: SaveVaultConfigRequest): Promise<SaveVaultConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.SaveVaultConfig(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Test auto-unseal connectivity
   */
  async TestAutoUnseal(data: TestAutoUnsealRequest): Promise<TestAutoUnsealResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.TestAutoUnseal(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Test LDAP connection
   */
  async TestLdapConnection(data: TestLdapConnectionRequest): Promise<TestLdapConnectionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.TestLdapConnection(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Test OIDC provider
   */
  async TestOidcProvider(data: TestOidcProviderRequest): Promise<TestOidcProviderResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.TestOidcProvider(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Validate platform slug
   */
  async ValidateSlug(params?: { slug?: string | number | boolean; }): Promise<ValidateSlugResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.ValidateSlug(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Validate wizard step
   */
  async ValidateStep(step_id: string, data: ValidateStepRequest): Promise<ValidateStepResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bootstrapApi.ValidateStep(step_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const bootstrapStore = new BootstrapStore();
